import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { WarningIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';
import { DuplicateDocumentInfo } from 'src/api/core/documents/useCheckDuplicateDocuments';

interface DuplicateFileWarningModalProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  duplicates: DuplicateDocumentInfo[];
  fileName: string;
}

export function DuplicateFileWarningModal({
  open,
  onClose,
  onContinue,
  duplicates,
  fileName,
}: DuplicateFileWarningModalProps) {
  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const handleViewAgreement = (agreementId: string) => {
    window.open(`/rebates/agreements/${agreementId}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <WarningIcon className="h-6 w-6 text-amber-500" />
            <DialogTitle className="text-xl">
              Duplicate File Detected
            </DialogTitle>
          </div>
          <DialogDescription className="text-base pt-2">
            The file <span className="font-semibold">{fileName}</span> has been
            previously uploaded and processed for rebate calculations.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-3">Previous Upload(s):</p>
            <div className="space-y-3">
              {duplicates.map((duplicate) => (
                <div
                  key={duplicate.documentId}
                  className="p-3 bg-gray-50 rounded-md border border-gray-200"
                >
                  <div className="space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm">
                          <span className="font-medium">Uploaded on:</span>{' '}
                          {formatDate(duplicate.uploadedAt)}
                        </p>
                        {duplicate.uploadedBy && (
                          <p className="text-sm">
                            <span className="font-medium">Uploaded by:</span>{' '}
                            {duplicate.uploadedBy}
                          </p>
                        )}
                        <p className="text-sm">
                          <span className="font-medium">File size:</span>{' '}
                          {formatFileSize(duplicate.fileSizeBytes)}
                        </p>
                      </div>
                    </div>

                    {duplicate.rebateAgreements &&
                      duplicate.rebateAgreements.length > 0 && (
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-sm font-medium mb-2">
                            Associated Rebate Agreement
                            {duplicate.rebateAgreements.length > 1 ? 's' : ''}:
                          </p>
                          <div className="space-y-2">
                            {duplicate.rebateAgreements.map((agreement) => (
                              <div
                                key={agreement.id}
                                className="flex items-start justify-between gap-2 bg-white p-2 rounded border border-gray-200"
                              >
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {agreement.name}
                                  </p>
                                  <p className="text-xs text-gray-600">
                                    {agreement.startDate} to {agreement.endDate}
                                  </p>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    handleViewAgreement(agreement.id)
                                  }
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 flex items-center gap-1 h-auto py-1 px-2"
                                >
                                  View
                                  <ArrowSquareOutIcon className="h-3 w-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
            <p className="text-sm text-amber-800">
              <span className="font-semibold">Warning:</span> This file was
              already used to create rebate calculations in the agreement(s)
              shown above. Reprocessing may create duplicate calculations. Only
              continue if you intend to reprocess this data.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="default"
            onClick={() => {
              onContinue();
              onClose();
            }}
            className="bg-amber-600 hover:bg-amber-700"
          >
            Continue Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
